// const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const API_BASE = process.env.REACT_APP_API_URL || 'https://api.3asmartcrackers.com/api';
const MEDIA_BASE = API_BASE.replace(/\/api\/?$/, '');

/** Rewrite stored product/media URLs (often localhost) to the live server. */
export function resolveMediaUrl(src) {
  if (!src) return src;
  try {
    if (/^https?:\/\//i.test(src)) {
      const u = new URL(src);
      if (u.pathname.startsWith('/uploads/')) {
        return `${MEDIA_BASE}${u.pathname}${u.search}`;
      }
      return src;
    }
  } catch (_) {
    /* fall through */
  }
  if (src.startsWith('/uploads/')) return `${MEDIA_BASE}${src}`;
  if (src.startsWith('uploads/')) return `${MEDIA_BASE}/${src}`;
  return src;
}

function getSessionId() {
  const key = 'sc_cart_session';
  let id = sessionStorage.getItem(key);
  if (!id) {
    id = `sc_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
    sessionStorage.setItem(key, id);
  }
  return id;
}

async function request(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, options);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data.message || `Request failed (${res.status})`);
  }
  return data;
}

export const api = {
  getSessionId,

  getCategories: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/categories${suffix}`);
  },
  getCategoriesWithCounts: () => request('/categories/with-counts'),
  createCategory: ({ category_name, status, imageFile }) => {
    const body = new FormData();
    body.append('category_name', category_name);
    body.append('status', String(status ?? 1));
    if (imageFile) body.append('category_image', imageFile);
    return request('/categories', { method: 'POST', body });
  },
  updateCategory: (id, { category_name, status, imageFile }) => {
    const body = new FormData();
    if (category_name !== undefined) body.append('category_name', category_name);
    if (status !== undefined) body.append('status', String(status));
    if (imageFile) body.append('category_image', imageFile);
    return request(`/categories/${id}`, { method: 'PUT', body });
  },

  getProducts: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/products${suffix}`);
  },
  createProduct: ({
    product_name,
    category_id,
    price,
    discount_price,
    youtube_link,
    unit,
    status,
    sortorder,
    imageFile,
  }) => {
    const body = new FormData();
    body.append('product_name', product_name);
    body.append('category_id', String(category_id));
    body.append('price', String(price));
    body.append('discount_price', String(discount_price));
    body.append('youtube_link', youtube_link || '');
    body.append('unit', unit || 'BOX');
    body.append('status', String(status ?? 1));
    if (sortorder !== undefined && sortorder !== null && sortorder !== '') {
      body.append('sortorder', String(sortorder));
    }
    if (imageFile) body.append('product_image', imageFile);
    return request('/products', { method: 'POST', body });
  },
  updateProduct: (
    id,
    {
      product_name,
      category_id,
      price,
      discount_price,
      youtube_link,
      unit,
      status,
      sortorder,
      imageFile,
    }
  ) => {
    const body = new FormData();
    if (product_name !== undefined) body.append('product_name', product_name);
    if (category_id !== undefined) body.append('category_id', String(category_id));
    if (price !== undefined) body.append('price', String(price));
    if (discount_price !== undefined) body.append('discount_price', String(discount_price));
    if (youtube_link !== undefined) body.append('youtube_link', youtube_link || '');
    if (unit !== undefined) body.append('unit', unit || 'BOX');
    if (status !== undefined) body.append('status', String(status));
    if (sortorder !== undefined && sortorder !== null && sortorder !== '') {
      body.append('sortorder', String(sortorder));
    }
    if (imageFile) body.append('product_image', imageFile);
    return request(`/products/${id}`, { method: 'PUT', body });
  },

  addToCart: (product_id, quantity = 1) =>
    request('/cart/add', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        product_id,
        quantity,
      }),
    }),

  updateCart: (product_id, quantity) =>
    request('/cart/update', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        product_id,
        quantity,
      }),
    }),

  removeFromCart: (product_id) =>
    request('/cart/remove', {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        session_id: getSessionId(),
        product_id,
      }),
    }),

  getCart: () => request(`/cart/${getSessionId()}`),

  placeOrder: async (form, items, paymentMethod = 'gpay') => {
    const body = new FormData();
    body.append('session_id', getSessionId());
    body.append('name', form.name);
    body.append('mobile', form.mobile);
    body.append('email', form.email);
    body.append('address', form.address);
    body.append('city', form.city);
    body.append('state', form.state);
    body.append('pincode', form.pincode);
    body.append('payment_method', paymentMethod);
    body.append(
      'items',
      JSON.stringify(
        items.map((i) => ({ product_id: i.id, quantity: i.qty }))
      )
    );

    const res = await fetch(`${API_BASE}/orders`, {
      method: 'POST',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || 'Failed to place order');
    }
    return data;
  },

  uploadPaymentProof: async (orderId, paymentFile, paymentMethod = 'gpay') => {
    const body = new FormData();
    if (paymentFile) body.append('payment_proof', paymentFile);
    body.append('payment_method', paymentMethod);

    const res = await fetch(`${API_BASE}/orders/${orderId}/payment-proof`, {
      method: 'PUT',
      body,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || 'Failed to upload payment proof');
    }
    return data;
  },

  getOrders: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/orders${suffix}`);
  },
  getOrder: (id) => request(`/orders/${id}`),
  downloadInvoicePdf: async (orderId, invoiceNo) => {
    const res = await fetch(`${API_BASE}/orders/${orderId}/invoice-pdf`);
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.message || 'Failed to download invoice');
    }
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `invoice-${invoiceNo || orderId}.pdf`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
  updateOrderStatus: (id, status) =>
    request(`/orders/${id}/status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    }),
  updatePaymentStatus: (id, payment_status) =>
    request(`/orders/${id}/payment-status`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ payment_status }),
    }),

  getDashboard: (params = {}) => {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const suffix = qs.toString() ? `?${qs}` : '';
    return request(`/admin/dashboard${suffix}`);
  },
};
